// components/UserComponents/PaymentMethods.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import paymentService from '../../services/paymentService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE);

/* -------------------------------- Utilities -------------------------------- */
const mask = (last4) => (last4 ? `•••• •••• •••• ${last4}` : '•••• •••• •••• ••••');
const expiry = (m, y) => (m && y ? `Expires ${String(m).padStart(2, '0')}/${y}` : '—');

const BrandBadge = ({ brand }) => {
  const label = (brand || 'CARD').toUpperCase();
  const gradient =
    label === 'VISA'
      ? 'linear-gradient(135deg, #2a2a2a, #4e4e4e)'
      : label.includes('MASTERCARD')
      ? 'linear-gradient(135deg, #ea4335, #fbbc05)'
      : 'linear-gradient(135deg, #343a40, #6c757d)';
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        color: 'white',
        fontWeight: 700,
        fontSize: 12,
        background: gradient,
        minWidth: 48,
        textAlign: 'center',
      }}
    >
      {label.includes('MASTERCARD') ? 'MC' : label}
    </Box>
  );
};

/* ---------------------------------- Row ----------------------------------- */
function PaymentRow({ pm, onRemove, onMakeDefault }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, backgroundColor: 'background.default' }}>
      <CardContent sx={{ py: 2.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <BrandBadge brand={pm.brand} />
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
                {mask(pm.last4)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {expiry(pm.exp_month, pm.exp_year)}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            {pm.is_default ? (
              <Chip label="Default" color="success" size="small" />
            ) : (
              <Tooltip title="Make default">
                <Button size="small" onClick={() => onMakeDefault(pm.id)}>
                  Set Default
                </Button>
              </Tooltip>
            )}
            <Tooltip title={pm.is_default ? "Default method can't be removed" : 'Remove card'}>
              <span>
                <IconButton
                  onClick={() => onRemove(pm.id)}
                  size="small"
                  disabled={pm.is_default}
                  sx={{
                    bgcolor: pm.is_default ? undefined : 'error.lighter',
                    '&:hover': pm.is_default ? undefined : { bgcolor: 'error.light' },
                  }}
                >
                  <DeleteOutlineIcon
                    fontSize="small"
                    sx={{ color: pm.is_default ? 'text.disabled' : 'error.main' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ---------- Add Card (PaymentElement + confirmSetup) ---------- */
function AddCardInner({ onAdded, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const handleAdd = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setMessage('');
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required', // only redirect for 3DS/etc
      });

      if (result?.error) {
        setMessage(result.error.message || 'Unable to confirm payment method.');
      } else if (result?.setupIntent?.status === 'succeeded') {
        onAdded?.();
      } else {
        setMessage('Unable to confirm payment method. Please try again.');
      }
    } catch (e) {
      setMessage(e?.message || 'An unexpected error occurred.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <PaymentElement id="payment-element" options={{ layout: 'tabs' }} />
      {message && <Alert severity="error">{message}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} variant="text">
          Cancel
        </Button>
        <Button onClick={handleAdd} variant="contained" disabled={!stripe || !elements || saving}>
          {saving ? 'Saving…' : 'Save Card'}
        </Button>
      </Box>
    </Stack>
  );
}

function AddCardDialog({ open, onClose, onAdded, userId }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siError, setSiError] = useState('');

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setSiError('');
      setClientSecret(null);
      try {
        // Your service already returns a setup intent with { clientSecret } (or client_secret)
        const resp = await paymentService.createSetupIntent(userId);
        const secret = resp?.clientSecret || resp?.client_secret;
        if (!secret) throw new Error('No client_secret returned from server');
        if (mounted) setClientSecret(secret);
      } catch (e) {
        if (mounted) setSiError(e?.message || 'Failed to initialize card entry.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, userId]);

  const body = useMemo(() => {
    if (siError) return <Alert severity="error">{siError}</Alert>;
    if (loading) {
      return (
        <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (!clientSecret) {
      return <Alert severity="error">Unable to initialize Stripe elements.</Alert>;
    }
    // IMPORTANT: Elements must receive options={{ clientSecret }} for PaymentElement
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
        <AddCardInner
          onAdded={() => {
            onClose?.();
            onAdded?.();
          }}
          onCancel={onClose}
        />
      </Elements>
    );
  }, [clientSecret, siError, loading, onAdded, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Card</DialogTitle>
      <DialogContent dividers>{body}</DialogContent>
    </Dialog>
  );
}

/* --------------------------------- Main ----------------------------------- */
function PaymentMethods({ userDetails }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [methods, setMethods] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const applyDefaultFlag = (list, defaultPaymentMethodId) =>
    (Array.isArray(list) ? list : []).map((pm) => ({
      ...pm,
      is_default: pm.id === defaultPaymentMethodId,
    }));

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const resp = await paymentService.getAvailableCards(userDetails?.id);
      const paymentMethods = resp?.paymentMethods ?? (Array.isArray(resp) ? resp : []);
      const defaultPaymentMethodId = resp?.defaultPaymentMethodId ?? null;
      setDefaultId(defaultPaymentMethodId);
      setMethods(applyDefaultFlag(paymentMethods, defaultPaymentMethodId));
    } catch (e) {
      setErr(e?.message || 'Failed to load methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-line react-hooks/exhaustive-deps */
  }, []);

  const handleMakeDefault = async (id) => {
    try {
      const resp = await paymentService.setDefaultCard(userDetails?.id, id);
      if (!resp?.success) throw new Error(resp?.message || 'Failed to set default');
      setDefaultId(id);
      setMethods((prev) => applyDefaultFlag(prev, id));
    } catch (e) {
      setErr(e.message || 'Failed to set default');
    }
  };

  const handleRemove = async (id) => {
    try {
      const resp = await paymentService.deleteCard(userDetails?.id, id);
      if (!resp?.success) throw new Error(resp?.message || 'Failed to remove card');
      setMethods((prev) => prev.filter((p) => p.id !== id));
      if (id === defaultId) {
        setDefaultId(null);
        setMethods((prev) => applyDefaultFlag(prev, null));
      }
    } catch (e) {
      setErr(e.message || 'Failed to remove card');
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Saved Payment Methods
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setAddOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Add New Card
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : err ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {err}
        </Alert>
      ) : (
        <>
          <Stack spacing={2} sx={{ mb: 3 }}>
            {methods.length === 0 ? (
              <Alert severity="info">No saved payment methods yet.</Alert>
            ) : (
              methods.map((pm) => (
                <PaymentRow
                  key={pm.id}
                  pm={pm}
                  onRemove={handleRemove}
                  onMakeDefault={handleMakeDefault}
                />
              ))
            )}
          </Stack>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <strong>Note:</strong> Your default payment method will be charged automatically for
            subscription renewals.
          </Alert>
        </>
      )}

      <AddCardDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={load}
        userId={userDetails?.id}
      />
    </Box>
  );
}

export default PaymentMethods;
